import { apiFetch } from './apiFetch'

// Request-coalescing loader for journey/exploration preview thumbnails.
//
// Each preview row used to fetch /api/task-thumbnails on its own, so a grid of N rows fired N
// (and, under React StrictMode, 2N) concurrent requests that saturated the browser's 6-connection
// limit and ballooned to 5-10s. This loader collects every loadThumbnails() call made within the
// same tick and issues ONE batched request per source type (?test_run_ids=a,b,c). Two rows asking
// for the same id share a single request, so duplicate fetches collapse too.

export interface ThumbnailFrame {
  url: string
  frame_index: number
}

export type ThumbnailSourceType = 'test_run' | 'exploration' | 'task'

// Plural query param per batchable source type. `task` isn't batched server-side (no row uses it),
// so it falls back to per-id single requests below.
const PLURAL_PARAM: Record<'test_run' | 'exploration', string> = {
  test_run: 'test_run_ids',
  exploration: 'exploration_ids',
}

interface Waiter {
  resolve: (frames: ThumbnailFrame[]) => void
  reject: (err: unknown) => void
}

const queues: Record<ThumbnailSourceType, Map<string, Waiter[]>> = {
  test_run: new Map(),
  exploration: new Map(),
  task: new Map(),
}
let scheduled = false

function schedule() {
  if (scheduled) return
  scheduled = true
  // Coalesce all loadThumbnails() calls in the current tick into one flush.
  setTimeout(flush, 0)
}

async function flushType(type: ThumbnailSourceType, queue: Map<string, Waiter[]>) {
  if (queue.size === 0) return
  const ids = [...queue.keys()]
  const waiters = new Map(queue)
  queue.clear()

  if (type === 'task') {
    await Promise.all(ids.map(async (id) => {
      const cbs = waiters.get(id) ?? []
      try {
        const res = await apiFetch(`/api/task-thumbnails?task_id=${encodeURIComponent(id)}`)
        if (!res.ok) throw new Error(`status ${res.status}`)
        const frames = (await res.json()) as ThumbnailFrame[]
        cbs.forEach((w) => w.resolve(frames))
      } catch (e) {
        cbs.forEach((w) => w.reject(e))
      }
    }))
    return
  }

  const param = PLURAL_PARAM[type]
  try {
    const res = await apiFetch(`/api/task-thumbnails?${param}=${ids.map(encodeURIComponent).join(',')}`)
    if (!res.ok) throw new Error(`status ${res.status}`)
    const data = (await res.json()) as Record<string, ThumbnailFrame[]>
    for (const [id, cbs] of waiters) cbs.forEach((w) => w.resolve(data[id] ?? []))
  } catch (e) {
    for (const [, cbs] of waiters) cbs.forEach((w) => w.reject(e))
  }
}

async function flush() {
  scheduled = false
  await Promise.all(
    (Object.keys(queues) as ThumbnailSourceType[]).map((t) => flushType(t, queues[t])),
  )
}

// Request the latest frame for one source; the actual network call is batched (one ?…_ids= request)
// with other same-tick callers. This is what the grid polls — the batched endpoint returns only the
// newest frame per source to keep the response small.
export function loadThumbnails(sourceType: ThumbnailSourceType, sourceId: string): Promise<ThumbnailFrame[]> {
  return new Promise<ThumbnailFrame[]>((resolve, reject) => {
    const queue = queues[sourceType]
    const arr = queue.get(sourceId) ?? []
    arr.push({ resolve, reject })
    queue.set(sourceId, arr)
    schedule()
  })
}

const SINGLE_PARAM: Record<ThumbnailSourceType, string> = {
  test_run: 'test_run_id',
  exploration: 'exploration_id',
  task: 'task_id',
}

// Fetch the FULL frame set for one source (the single-id endpoint returns every frame). Used on
// hover to enable the frame-cycling preview — not batched, since it's one row on demand.
export async function loadFullThumbnails(sourceType: ThumbnailSourceType, sourceId: string): Promise<ThumbnailFrame[]> {
  const res = await apiFetch(`/api/task-thumbnails?${SINGLE_PARAM[sourceType]}=${encodeURIComponent(sourceId)}`)
  if (!res.ok) throw new Error(`status ${res.status}`)
  return (await res.json()) as ThumbnailFrame[]
}
